import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Calendar, Clock, MapPin, Users, Coins, Image as ImageIcon, MessageCircle, Send, Save, Ticket } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useCircleHostEvents } from "@/hooks/useCircleHostEvents";
import { useCircleConfig } from "@/hooks/useCircleConfig";
import { CircleEvent } from "@/types/circle";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface CircleEventFormProps {
  event?: CircleEvent;
  onSuccess?: () => void;
}

export function CircleEventForm({ event, onSuccess }: CircleEventFormProps) {
  const { createEvent, updateEvent } = useCircleHostEvents();
  const { config } = useCircleConfig();
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  
  const isEditing = !!event;
  
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [venue, setVenue] = useState("");
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const [capacity, setCapacity] = useState("10");
  const [imageUrl, setImageUrl] = useState("");
  const [whatsappGroup, setWhatsappGroup] = useState("");
  const [ticketBookingUrl, setTicketBookingUrl] = useState("");

  // Populate form when editing
  useEffect(() => {
    if (event) {
      setTitle(event.title);
      setDescription(event.description || "");
      setVenue(event.venue);
      setStartDateTime(format(new Date(event.start_time), "yyyy-MM-dd'T'HH:mm"));
      setEndDateTime(format(new Date(event.end_time), "yyyy-MM-dd'T'HH:mm"));
      setCapacity(String(event.capacity));
      setImageUrl(event.image_url || "");
      setWhatsappGroup(event.whatsapp_group || "");
      setTicketBookingUrl(event.ticket_booking_url || "");
    }
  }, [event]);

  // Auto-set end time to 3 hours after start
  const handleStartDateTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDateTime = e.target.value;
    setStartDateTime(newStartDateTime);
    
    if (newStartDateTime) {
      const startDate = new Date(newStartDateTime);
      const endDate = new Date(startDate.getTime() + 3 * 60 * 60 * 1000);
      const formattedEnd = format(endDate, "yyyy-MM-dd'T'HH:mm");
      
      if (!endDateTime || new Date(endDateTime) <= startDate) {
        setEndDateTime(formattedEnd);
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!title || !venue || !startDateTime || !endDateTime) {
      return;
    }

    // Validate ticket booking URL if provided
    if (ticketBookingUrl) {
      try {
        new URL(ticketBookingUrl);
      } catch {
        toast({
          title: "Invalid Ticket Booking Link",
          description: "Please enter a valid URL (e.g. https://...)",
          variant: "destructive",
        });
        return;
      }
    }

    const eventData = {
      title,
      description: description || undefined,
      venue,
      start_time: new Date(startDateTime).toISOString(),
      end_time: new Date(endDateTime).toISOString(),
      capacity: parseInt(capacity) || 10,
      credit_cost: config?.credit_cost ?? 1,
      image_url: imageUrl || undefined,
      whatsapp_group: whatsappGroup || undefined,
      ticket_booking_url: ticketBookingUrl || undefined,
    };

    if (isEditing && event) {
      updateEvent.mutate(
        { eventId: event.id, updates: eventData },
        { onSuccess: () => onSuccess?.() }
      );
    } else {
      createEvent.mutate(eventData, {
        onSuccess: () => {
          setTitle("");
          setDescription("");
          setVenue("");
          setStartDateTime("");
          setEndDateTime("");
          setCapacity("10");
          setImageUrl("");
          setWhatsappGroup("");
          setTicketBookingUrl("");
          onSuccess?.();
        },
      });
    }
  };

  const isValid = title && venue && startDateTime && endDateTime;
  const isPending = createEvent.isPending || updateEvent.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Calendar className="w-5 h-5 text-violet-500" />
          {isEditing ? "Edit Event" : "Lead an Event"}
        </CardTitle>
        <CardDescription>
          {isEditing 
            ? "Update your Circle event details."
            : "Create a Circle event for the community. Your event will be reviewed before publishing."
          }
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Event Title *</Label>
            <Input
              id="title"
              placeholder="e.g., Gallery Visit at Tate Modern"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Tell people what to expect..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="venue" className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              Venue *
            </Label>
            <Input
              id="venue"
              placeholder="e.g., Tate Modern, London"
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-2">
              <Label htmlFor="startDateTime" className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Start *
              </Label>
              <Input
                id="startDateTime"
                type="datetime-local"
                value={startDateTime}
                onChange={handleStartDateTimeChange}
                min={isEditing ? undefined : format(new Date(), "yyyy-MM-dd'T'HH:mm")}
                required
                className="text-left relative [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [color-scheme:light] dark:[color-scheme:dark]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDateTime" className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                End *
              </Label>
              <Input
                id="endDateTime"
                type="datetime-local"
                value={endDateTime}
                onChange={(e) => setEndDateTime(e.target.value)}
                required
                className="text-left relative [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [color-scheme:light] dark:[color-scheme:dark]"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="capacity" className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                Capacity
              </Label>
              <Input
                id="capacity"
                type="number"
                min="1"
                max="100"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Coins className="w-4 h-4" />
                Credit Cost
              </Label>
              <Input
                type="number"
                value={config?.credit_cost ?? 1}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">Set by admin</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              <ImageIcon className="w-4 h-4" />
              Event Image (optional)
            </Label>
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="outline"
                className="relative"
                disabled={uploading}
              >
                <input
                  type="file"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  accept="image/*"
                  onChange={async (e) => {
                    try {
                      setUploading(true);
                      if (!e.target.files || e.target.files.length === 0) {
                        return;
                      }
                      const file = e.target.files[0];
                      const fileExt = file.name.split('.').pop();
                      const filePath = `circle-events/${Math.random()}.${fileExt}`;
                      
                      const { error: uploadError } = await supabase.storage
                        .from('avatars')
                        .upload(filePath, file);
                      
                      if (uploadError) throw uploadError;
                      
                      const { data: { publicUrl } } = supabase.storage
                        .from('avatars')
                        .getPublicUrl(filePath);
                      
                      setImageUrl(publicUrl);
                      toast({
                        title: "Success",
                        description: "Image uploaded successfully",
                      });
                    } catch (error) {
                      toast({
                        title: "Error",
                        description: "Error uploading image",
                        variant: "destructive",
                      });
                    } finally {
                      setUploading(false);
                    }
                  }}
                  disabled={uploading}
                />
                <ImageIcon className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload Image'}
              </Button>
              {imageUrl && (
                <span className="text-sm text-muted-foreground">
                  Image uploaded
                </span>
              )}
            </div>
            {imageUrl && (
              <div className="relative w-full h-32 rounded-lg overflow-hidden mt-2">
                <img
                  src={imageUrl}
                  alt="Event preview"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="whatsapp" className="flex items-center gap-1">
              <MessageCircle className="w-4 h-4" />
              WhatsApp Group Link (optional)
            </Label>
            <Input
              id="whatsapp"
              type="url"
              placeholder="https://chat.whatsapp.com/..."
              value={whatsappGroup}
              onChange={(e) => setWhatsappGroup(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Attendees will see this after booking
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ticketUrl" className="flex items-center gap-1">
              <Ticket className="w-4 h-4" />
              Ticket Booking Link (optional)
            </Label>
            <Input
              id="ticketUrl"
              type="url"
              placeholder="https://www.example.com/book-tickets"
              value={ticketBookingUrl}
              onChange={(e) => setTicketBookingUrl(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              If your event requires a separate ticket purchase (e.g. cinema, venue), paste the link here. Leave blank if not needed.
            </p>
          </div>

          <Button
            type="submit"
            disabled={!isValid || isPending}
            className="w-full bg-violet-500 hover:bg-violet-600"
          >
            {isPending ? (
              <>
                <LoadingSpinner className="w-4 h-4 mr-2" />
                {isEditing ? "Saving..." : "Creating..."}
              </>
            ) : (
              <>
                {isEditing ? <Save className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                {isEditing ? "Save Changes" : "Submit for Review"}
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
